import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { CockpitTask } from '@/lib/homeSummaries';

// Project Cockpit data: open tasks with their assignee, and the signed-in user's display name so "assigned to me"
// can match the assignee names that synced tasks carry. Tenant data, RLS-scoped.
export function useCockpitData() {
  const { user } = useAuth();

  const tasks = useQuery({
    queryKey: ['home', 'cockpit', 'tasks'],
    staleTime: 60_000,
    queryFn: async (): Promise<CockpitTask[]> => {
      const { data, error } = await supabase.from('tasks_sync')
        .select('id,external_id,title,status,priority,assignee,due_date,project_external_id')
        .neq('status', 'done').order('due_date', { ascending: true }).limit(500);
      if (error) throw new Error(error.message);
      return (data ?? []) as CockpitTask[];
    },
  });
  const profile = useQuery({
    queryKey: ['home', 'cockpit', 'profile', user?.id],
    enabled: !!user?.id,
    staleTime: 300_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.from('profiles').select('display_name').eq('user_id', user!.id).maybeSingle();
      if (error) throw new Error(error.message);
      return data?.display_name ?? null;
    },
  });
  return {
    tasks: tasks.data ?? [], tasksFailed: tasks.isError,
    /** The name tasks are matched against; null when the profile has no display name. */
    myName: profile.data ?? null,
    isLoading: tasks.isLoading,
  };
}
